import React from "react";
import { Sparkles } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  FieldGroup, FormField, Input, Radio, Textarea,
} from "../../ui";
import { LIMITS, defaultInstructions, isPublicOnly } from "./builderRules";

/**
 * Step 2 — what the assessment is called, what kind it is, and what students
 * read before they start.
 *
 * Until the author types their own, the instructions are written from the
 * paper itself (builderRules.defaultInstructions) and follow every change to
 * the sections and rules — so a new author gets correct instructions without
 * writing a word, and an author who wrote their own never has them replaced.
 */
export default function DetailsStep({
  form, set, setForm, onTypeChange, errors, locked, codingShown, usageChoice = null,
}) {
  const suggested = defaultInstructions(form);
  const shown = form.instructionsTouched ? form.instructions : suggested;
  const publicOnly = isPublicOnly(form);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {usageChoice}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Name it</AppCardTitle>
          <AppCardDescription>
            {publicOnly
              ? "For you to find it when you set up a public exam. Candidates see the public exam's own title."
              : "Students see the name and the description on their list of assessments."}
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <FormField label="Name" required error={errors.title}>
            <Input
              size="lg"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Campus drive mock test 1, Python unit test, ICD-10 practice set"
              maxLength={LIMITS.title}
              disabled={locked}
              autoFocus={!form.title}
            />
          </FormField>

          {/* A public exam decides what candidates see afterwards, so the kind
              would set nothing there. */}
          {!publicOnly && (
            <FieldGroup legend="Kind" hint="Sets sensible defaults for what students see afterwards. You can change each one in Rules.">
              <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
                <Radio
                  variant="card"
                  name="assessment-kind"
                  checked={form.type === "practice"}
                  onChange={() => onTypeChange("practice")}
                  disabled={locked}
                  label="Practice"
                  description="For learning — students review their answers with the right ones and explanations."
                />
                <Radio
                  variant="card"
                  name="assessment-kind"
                  checked={form.type === "exam"}
                  onChange={() => onTypeChange("exam")}
                  disabled={locked}
                  label="Exam"
                  description="For marks — the score, without handing out the answer key."
                />
              </div>
            </FieldGroup>
          )}

          <FormField label="Description" hint="Optional. One line about what it covers.">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder={`e.g. Aptitude, ${codingShown ? "coding" : "reasoning"} and technical rounds for the November drive`}
              maxLength={1000}
            />
          </FormField>
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>{publicOnly ? "Instructions for candidates" : "Instructions for students"}</AppCardTitle>
          <AppCardDescription>
            {publicOnly
              ? "Shown before they start, unless the public exam has instructions of its own."
              : "Shown before they start, and from the exam screen."}
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
          <FormField
            label="Instructions"
            hint={form.instructionsTouched
              ? "Your own words. They no longer change when you change the paper."
              : "Written from your setup, and kept up to date as you change sections and rules. Edit it to make it your own."}
          >
            <Textarea
              rows={12}
              value={shown}
              onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value, instructionsTouched: true }))}
              maxLength={5000}
            />
          </FormField>
          {form.instructionsTouched && shown !== suggested && (
            <div>
              <AppButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm((f) => ({ ...f, instructions: suggested, instructionsTouched: false }))}
              >
                <Sparkles size={16} aria-hidden="true" /> Use the suggested instructions
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    </div>
  );
}
